import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import {
  SETTINGS_TABS,
  type SettingsTabId,
} from "@/features/settings/utils/settingsTabs";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";
import { getNextTabIndex } from "@/shared/lib/tabListKeyboardNavigation";

export interface SettingsTabsState {
  readonly activeTab: SettingsTabId;
  readonly isOpen: boolean;
}

export type SettingsTabsEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/tab-selected"
      | "settings/tab-key-pressed"
      | "settings/modal-visibility-changed";
  }
>;

export function createSettingsTabsMediator(
  isOpen = true,
): Mediator<SettingsTabsState, SettingsTabsEvent> {
  return createMediator<SettingsTabsState, SettingsTabsEvent>(
    { activeTab: "display", isOpen },
    (state, event) => {
      switch (event.type) {
        case "settings/tab-selected":
          return {
            status: "handled",
            state: { ...state, activeTab: event.tabId },
          };
        case "settings/tab-key-pressed": {
          const activeIndex = SETTINGS_TABS.findIndex(
            (tab) => tab.id === state.activeTab,
          );
          const nextIndex = getNextTabIndex(
            activeIndex,
            event.key,
            SETTINGS_TABS.length,
          );
          const nextTab =
            nextIndex === null ? undefined : SETTINGS_TABS[nextIndex];
          if (!nextTab) return { status: "unhandled" };
          return {
            status: "handled",
            state: { ...state, activeTab: nextTab.id },
          };
        }
        case "settings/modal-visibility-changed":
          return {
            status: "handled",
            state: {
              activeTab:
                event.isOpen && !state.isOpen ? "display" : state.activeTab,
              isOpen: event.isOpen,
            },
          };
      }
    },
  );
}
